import {
  BookOpenText,
  Calculator,
  CalendarRange,
  CircleDollarSign,
  Download,
  FileDown,
  FilePlus2,
  FolderKanban,
  LayoutDashboard,
  Menu,
  PanelLeftClose,
  ReceiptText,
  Settings,
  SlidersHorizontal,
  Upload,
} from "lucide-react";
import { useMemo, useRef, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { calculateQuote, currency, numberOne, type QuoteCalculation } from "../calculations";
import { cloneProject, downloadJson, parseImport, toBackup } from "../storage";
import { usePricingStore } from "../store";
import type { PricingBackupV1 } from "../types";
import { Modal } from "./Modal";

const navItems = [
  { path: "/", label: "总览", icon: LayoutDashboard },
  { path: "/wbs", label: "功能 WBS", icon: SlidersHorizontal },
  { path: "/platforms", label: "平台拆分", icon: FolderKanban },
  { path: "/addons", label: "附加费用", icon: ReceiptText },
  { path: "/monthly", label: "包月支持", icon: CalendarRange },
  { path: "/settings", label: "参数设置", icon: Settings },
  { path: "/cases", label: "参考案例", icon: BookOpenText },
];

function ImportDialog({ backup, onClose }: { backup: PricingBackupV1; onClose: () => void }) {
  const { dispatch } = usePricingStore();
  return (
    <Modal title="导入完整备份" onClose={onClose}>
      <div className="modal-body stack">
        <p className="muted">已验证 {backup.projects.length} 个项目。默认合并；替换会删除浏览器中现有项目。</p>
        <div className="modal-actions">
          <button className="button secondary" type="button" onClick={onClose}>取消</button>
          <button className="button secondary" type="button" onClick={() => { dispatch({ type: "import-replace", backup }); onClose(); }}>替换全部</button>
          <button className="button primary" type="button" onClick={() => { dispatch({ type: "import-merge", backup }); onClose(); }}>合并（推荐）</button>
        </div>
      </div>
    </Modal>
  );
}

export function AppLayout({ children, calculation }: { children: ReactNode; calculation: QuoteCalculation }) {
  const navigate = useNavigate();
  const location = useLocation();
  const fileRef = useRef<HTMLInputElement>(null);
  const { state, activeProject, dispatch, createNewProject } = usePricingStore();
  const [pendingBackup, setPendingBackup] = useState<PricingBackupV1 | null>(null);
  const [mobileInspectorOpen, setMobileInspectorOpen] = useState(false);
  const savedLabel = useMemo(
    () => new Intl.DateTimeFormat("zh-CN", { hour: "2-digit", minute: "2-digit", second: "2-digit" }).format(new Date(state.savedAt)),
    [state.savedAt],
  );

  const importFile = async (file: File) => {
    try {
      const parsed = parseImport(JSON.parse(await file.text()));
      if (parsed.kind === "outter-demo-calc-backup") {
        setPendingBackup(parsed);
      } else {
        const project = cloneProject(parsed.project, "（导入）");
        dispatch({ type: "add-project", project });
        navigate("/wbs");
      }
    } catch (error) {
      window.alert(error instanceof Error ? error.message : "导入失败。 ");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const create = () => {
    createNewProject();
    navigate("/wbs");
  };

  const exportBackup = () => downloadJson(toBackup(state), `技术外包报价备份-${new Date().toISOString().slice(0, 10)}.json`);
  const openQuote = () => calculation.canPrint && navigate("/quote");

  return (
    <div className="app-shell">
      <header className="topbar">
        <button className="brand" type="button" onClick={() => navigate("/")} aria-label="返回总览">
          <span className="brand-mark"><Calculator size={22} strokeWidth={2.2} /></span>
          <span>技术外包报价计算器</span>
        </button>
        <span className="autosave"><i /> 已自动保存 {savedLabel}</span>
        <div className="topbar-actions">
          <button className="button secondary" type="button" onClick={create}><FilePlus2 size={16} />新建报价</button>
          <button className="button secondary" type="button" onClick={() => fileRef.current?.click()}><Upload size={16} />导入</button>
          <button className="button secondary" type="button" onClick={exportBackup}><Download size={16} />导出 JSON</button>
          <button className="button primary" type="button" onClick={openQuote} disabled={!calculation.canPrint} title={calculation.canPrint ? "打开客户报价打印页" : calculation.statusMessage}>
            <FileDown size={16} />导出客户报价 PDF
          </button>
        </div>
        <button className="mobile-menu-button" type="button" aria-label="更多操作" onClick={exportBackup}><Menu size={22} /></button>
        <input ref={fileRef} hidden type="file" accept="application/json,.json" onChange={(event) => event.target.files?.[0] && void importFile(event.target.files[0])} />
      </header>

      <nav className="mobile-tabs" aria-label="移动端导航">
        {navItems.slice(0, 6).map(({ path, label }) => (
          <button key={path} type="button" className={location.pathname === path ? "active" : ""} onClick={() => navigate(path)}>
            {label.replace("功能 ", "")}
          </button>
        ))}
      </nav>

      <div className="app-body">
        <aside className="sidebar">
          <nav aria-label="主导航">
            {navItems.map(({ path, label, icon: Icon }) => (
              <button key={path} type="button" className={location.pathname === path ? "active" : ""} onClick={() => navigate(path)}>
                <Icon size={18} /><span>{label}</span>
              </button>
            ))}
          </nav>
          <button className="collapse-hint" type="button"><PanelLeftClose size={17} />收起导航</button>
        </aside>
        <main className="workspace">{children}</main>
        <PricingInspector calculation={calculation} />
      </div>

      <div className="mobile-summary-bar">
        <button type="button" className="mobile-rate" onClick={() => setMobileInspectorOpen(true)}>
          当前单价 {calculation.actualDayRate === null ? "n.a." : currency(calculation.actualDayRate)} / {calculation.status}
        </button>
        <div><span>总价</span><strong>{currency(calculation.status === "仅需求梳理" ? calculation.discoveryTotal : calculation.customerTotal)}</strong></div>
        <button className="button primary" type="button" onClick={() => setMobileInspectorOpen(true)}><CircleDollarSign size={17} />查看核价</button>
      </div>
      {mobileInspectorOpen ? (
        <div className="mobile-inspector-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && setMobileInspectorOpen(false)}>
          <div className="mobile-inspector-sheet">
            <div className="sheet-handle" />
            <button className="sheet-close" type="button" onClick={() => setMobileInspectorOpen(false)}>关闭</button>
            <PricingInspector calculation={calculation} mobile />
          </div>
        </div>
      ) : null}
      {pendingBackup ? <ImportDialog backup={pendingBackup} onClose={() => setPendingBackup(null)} /> : null}
    </div>
  );
}

export function PricingInspector({ calculation, mobile = false }: { calculation: QuoteCalculation; mobile?: boolean }) {
  const { activeProject, state, updateActiveProject } = usePricingStore();
  const statusClass = calculation.status === "可报价" || calculation.status === "仅需求梳理" ? "success" : "danger";
  const activeTotal = calculation.status === "仅需求梳理" ? calculation.discoveryTotal : calculation.customerTotal;

  return (
    <aside className={mobile ? "pricing-inspector mobile" : "pricing-inspector"}>
      <h2><span />核价设置</h2>
      <div className="inspector-fields">
        <label>需求状态<select value={activeProject.demandStatus} onChange={(event) => updateActiveProject({ demandStatus: event.target.value as typeof activeProject.demandStatus })}><option>清晰</option><option>少量不确定</option><option>不清晰</option></select></label>
        {activeProject.demandStatus === "不清晰" ? (
          <label>梳理人日<input type="number" min={2} max={5} value={activeProject.discoveryDays} onChange={(event) => updateActiveProject({ discoveryDays: Math.min(5, Math.max(2, Number(event.target.value))) })} /></label>
        ) : null}
        <label>商业折扣<div className="unit-input"><input type="number" min={0} max={99} value={activeProject.discountRate * 100} onChange={(event) => updateActiveProject({ discountRate: Math.min(0.99, Math.max(0, Number(event.target.value) / 100)) })} /><span>%</span></div></label>
        <label>开票附加<div className="unit-input"><input type="number" min={0} value={activeProject.taxRate * 100} onChange={(event) => updateActiveProject({ taxRate: Math.max(0, Number(event.target.value) / 100) })} /><span>%</span></div></label>
        <label>准备金<div className="unit-input"><input readOnly value={(calculation.reserveRate * 100).toFixed(0)} /><span>%</span></div></label>
        <label>年度维护<select value={activeProject.maintenanceOption} onChange={(event) => updateActiveProject({ maintenanceOption: event.target.value as typeof activeProject.maintenanceOption })}><option>无</option><option>基础维护</option><option>标准维护</option></select></label>
      </div>

      <div className="redline-card">
        <span>内部红线</span>
        <strong>{currency(state.settings.redlineRate)} / 人日</strong>
        <small>低于此单价禁止生成正式报价</small>
      </div>
      <div className={`status-card ${statusClass}`}>
        <span>当前单价</span>
        <strong>{calculation.actualDayRate === null ? "n.a." : `${currency(calculation.actualDayRate)} / ${calculation.status}`}</strong>
        <small>{calculation.statusMessage}</small>
      </div>

      <section className="price-breakdown">
        <h3>价格构成</h3>
        <dl>
          <div><dt>调整后人工费</dt><dd>{currency(calculation.adjustedLabor)}</dd></div>
          <div><dt>商业折扣</dt><dd>- {currency(calculation.discountAmount)}</dd></div>
          <div><dt>准备金</dt><dd>+ {currency(calculation.reserveAmount)}</dd></div>
          <div><dt>第三方报价</dt><dd>+ {currency(calculation.addonCustomerAmount)}</dd></div>
          <div><dt>开票附加</dt><dd>+ {currency(calculation.taxAmount)}</dd></div>
          <div className="grand-total"><dt>客户总价</dt><dd>{currency(activeTotal)}</dd></div>
        </dl>
      </section>
      {activeProject.maintenanceOption !== "无" ? <div className="maintenance-note"><span>{activeProject.maintenanceOption}（独立可选）</span><strong>{currency(activeProject.maintenanceOption === "基础维护" ? calculation.maintenanceBasic : calculation.maintenanceStandard)} / 年</strong><small>不默认并入开发总价</small></div> : null}
      <label className="notes-field">备注（可选）<textarea maxLength={200} value={activeProject.notes} onChange={(event) => updateActiveProject({ notes: event.target.value })} placeholder="如有特殊说明，可在此填写…" /><small>{activeProject.notes.length}/200</small></label>
    </aside>
  );
}

export function ProjectHeader({ calculation }: { calculation: QuoteCalculation }) {
  const { activeProject, updateActiveProject } = usePricingStore();
  return (
    <>
      <section className="project-strip">
        <label>项目名称<input value={activeProject.projectName} onChange={(event) => updateActiveProject({ projectName: event.target.value })} /></label>
        <label>项目描述（可选）<div><input maxLength={200} value={activeProject.description} onChange={(event) => updateActiveProject({ description: event.target.value })} placeholder="用于记录项目背景、核心目标、范围等…" /><small>{activeProject.description.length}/200</small></div></label>
      </section>
      <section className="metric-strip" aria-label="报价摘要">
        <article><span>客户总价</span><strong>{currency(calculation.status === "仅需求梳理" ? calculation.discoveryTotal : calculation.customerTotal)}</strong></article>
        <article><span>预计人日</span><strong>{numberOne(calculation.actualDays)} <small>人日</small></strong></article>
        <article><span>实际人日单价</span><strong>{calculation.actualDayRate === null ? "n.a." : currency(calculation.actualDayRate)}</strong></article>
        <article><span>报价状态</span><strong className={calculation.canPrint ? "text-success" : "text-danger"}>{calculation.status}</strong></article>
      </section>
    </>
  );
}
